import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminAPI, uploadImageToCloudinary } from '../services/api';
import AdminLayout from '../components/AdminLayout';
import SortableImageGrid from '../components/SortableImageGrid';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { Save, ArrowLeft, Loader2, Sparkles } from 'lucide-react';

const EMPTY = {
  title: '',
  description: 'visit airbnb page for all the property info.',
  airbnb_url: '',
};

const PropertyForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [form, setForm] = useState(EMPTY);
  const [images, setImages] = useState([]);
  const [mainImageIndex, setMainImageIndex] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    adminAPI
      .listProperties()
      .then((all) => {
        const p = all.find((x) => x.id === id);
        if (!p) {
          toast.error('Property not found');
          navigate('/admin/properties');
          return;
        }
        setForm({
          title: p.title || '',
          description: p.description || '',
          airbnb_url: p.airbnb_url || '',
        });
        const imgs = [p.main_image, ...(p.all_images || [])].filter(Boolean);
        setImages([...new Set(imgs)]);
        setMainImageIndex(0);
      })
      .catch(() => toast.error('Failed to load property'))
      .finally(() => setLoading(false));
  }, [id, isEdit, navigate]);

  const onField = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
  };

  const handleUpload = useCallback(
    async (e) => {
      const files = Array.from(e.target.files || []);
      if (!files.length) return;
      if (images.length + files.length > 50) {
        toast.error(`Maximum 50 images. You have ${images.length}, tried to add ${files.length}.`);
        return;
      }
      setUploading(true);
      try {
        const uploaded = [];
        for (const f of files) {
          if (f.size > 10 * 1024 * 1024) {
            toast.error(`${f.name} exceeds 10MB`);
            continue;
          }
          const url = await uploadImageToCloudinary(f);
          uploaded.push(url);
        }
        setImages((prev) => [...prev, ...uploaded]);
        if (uploaded.length) toast.success(`Uploaded ${uploaded.length} image(s)`);
      } catch (err) {
        toast.error('Upload failed — check Cloudinary config');
      } finally {
        setUploading(false);
        e.target.value = '';
      }
    },
    [images]
  );

  const handleImportFromAirbnb = async () => {
    const url = form.airbnb_url.trim();
    if (!url) {
      toast.error('Paste an airbnb URL first');
      return;
    }
    setImporting(true);
    const t = toast.loading('Fetching from airbnb — this can take up to a minute...');
    try {
      const data = await adminAPI.importFromAirbnb(url);
      toast.dismiss(t);

      const nextForm = { ...form, airbnb_url: data.airbnb_url || url };
      if (data.title) nextForm.title = data.title;
      // Description is intentionally NOT auto-filled — we default to a
      // fixed message and let the admin edit if needed.
      setForm(nextForm);

      if (Array.isArray(data.photos) && data.photos.length) {
        setImages((prev) => {
          const merged = [...prev, ...data.photos];
          return [...new Set(merged)].slice(0, 50);
        });
      }

      const foundBits = [];
      if (data.title) foundBits.push('title');
      if (data.photos && data.photos.length) foundBits.push(`${data.photos.length} photos`);

      if (foundBits.length === 0) {
        toast.warning('Could not extract any details. Please enter them manually.');
      } else {
        toast.success(`Imported ${foundBits.join(', ')} from airbnb. Review and edit before saving.`);
      }
    } catch (error) {
      toast.dismiss(t);
      toast.error(error.response?.data?.detail || 'Failed to import from airbnb');
    } finally {
      setImporting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) {
      toast.error('Title and description are required');
      return;
    }
    if (!form.airbnb_url.trim()) {
      toast.error('airbnb URL is required');
      return;
    }
    if (images.length === 0) {
      toast.error('Please upload at least one photo');
      return;
    }

    setSaving(true);
    try {
      const ordered = [images[mainImageIndex], ...images.filter((_, i) => i !== mainImageIndex)];
      // Backend model still expects city/location — provide harmless defaults
      // so the record stays valid on the server side.
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        airbnb_url: form.airbnb_url.trim(),
        city: 'N/A',
        location: 'N/A',
        main_image: ordered[0],
        all_images: ordered,
      };

      if (isEdit) {
        await adminAPI.updateProperty(id, payload);
        toast.success('Property updated');
      } else {
        await adminAPI.createProperty(payload);
        toast.success('Property created');
      }
      navigate('/admin/properties');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save property');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <p className="text-gray-400">Loading...</p>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-3xl mx-auto space-y-6">
        <button
          onClick={() => navigate('/admin/properties')}
          className="text-sm text-gray-400 hover:text-white flex items-center gap-1"
        >
          <ArrowLeft className="w-4 h-4" /> Back to properties
        </button>
        <h1 className="text-2xl font-bold text-white">{isEdit ? 'Edit property' : 'Add new property'}</h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-6 space-y-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Title <span className="text-red-400">*</span></Label>
                <Input
                  value={form.title}
                  onChange={onField('title')}
                  className="bg-gray-900 border-gray-700 text-white"
                  placeholder="e.g. Cozy hillside cottage in Mussoorie"
                  data-testid="pf-title"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Description <span className="text-red-400">*</span></Label>
                <Textarea
                  value={form.description}
                  onChange={onField('description')}
                  className="bg-gray-900 border-gray-700 text-white min-h-[140px]"
                  placeholder="Describe the vibe, the space, and what makes this stay special..."
                  data-testid="pf-description"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">airbnb URL <span className="text-red-400">*</span></Label>
                <div className="flex gap-2">
                  <Input
                    value={form.airbnb_url}
                    onChange={onField('airbnb_url')}
                    className="bg-gray-900 border-gray-700 text-white flex-1"
                    placeholder="https://www.airbnb.co.in/rooms/..."
                    data-testid="pf-airbnb-url"
                  />
                  <Button
                    type="button"
                    onClick={handleImportFromAirbnb}
                    disabled={importing || !form.airbnb_url.trim()}
                    className="bg-purple-600 hover:bg-purple-700 whitespace-nowrap"
                    data-testid="pf-import-btn"
                  >
                    {importing ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Fetching...</>
                    ) : (
                      <><Sparkles className="w-4 h-4 mr-2" /> Fetch from airbnb</>
                    )}
                  </Button>
                </div>
                <p className="text-xs text-gray-500">
                  Paste an airbnb URL and click &quot;Fetch from airbnb&quot; to auto-fill title and photos.
                  You can still edit anything before saving.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-6 space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-white">Photos</h2>
                <p className="text-xs text-gray-400 mt-1">
                  Up to 50 photos. Drag to reorder. Click the star icon to set the main (cover) photo.
                </p>
              </div>
              <SortableImageGrid
                images={images}
                onImagesChange={setImages}
                mainImageIndex={mainImageIndex}
                onMainImageChange={setMainImageIndex}
                uploading={uploading}
                onUpload={handleUpload}
              />
            </CardContent>
          </Card>

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              className="border-gray-600 text-gray-200 hover:bg-gray-800"
              onClick={() => navigate('/admin/properties')}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-red-600 hover:bg-red-700"
              data-testid="pf-save-btn"
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
              ) : (
                <><Save className="w-4 h-4 mr-2" /> {isEdit ? 'Update property' : 'Create property'}</>
              )}
            </Button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default PropertyForm;
